/**
 * Authentication Context using Supabase Auth
 *
 * Manages user authentication state using Supabase for identity management
 * while storing user profile data in our PostgreSQL database.
 *
 * Session lifecycle (WEBD-03): whenever a session ends — logout, Supabase
 * SIGNED_OUT, a failed token refresh, an invalid refresh token, or the API
 * client's 401-after-refresh — `endSession()` cancels and clears the React
 * Query cache (queries and mutations) and the user-scoped localStorage keys.
 * A sign-in as a different user than the last one runs the same clear before
 * the new session is published, so no query of the previous user can serve
 * the next one.
 *
 * One source of truth for the user (WEBD-24): this context owns only the
 * session. The profile lives in React Query under `userKeys.profile()`; the
 * provider warms that entry when a session starts (`ensureQueryData`, so a
 * component's `useUserProfile()` shares the same request) and every reader —
 * tier gating, admin UI, tax settings — uses `useCurrentUser()` /
 * `useUserProfile()`, so a settings or tier change is seen everywhere.
 *
 * A password sign-in needs the authenticator code (see ./mfa): it yields an
 * aal1 session, which is held at `mfaStep` ('enroll' or 'challenge') with
 * `isAuthenticated` false — so nothing calls the API, which rejects a
 * password-only session — until a TOTP code is verified and the aal2 session
 * is published. A passkey sign-in is signed in straight away.
 */

import { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import { authApi } from '../api/authApi';
import { setTokenGetter, setTokenRefresher, setLogoutHandler } from '../api/client';
import { userKeys } from '../api/queryKeys';
import { userProfileQueryOptions } from '../hooks/useUserProfile';
import { isTwoFactorSession, mfaStepFor, signedInFromEmailLink, verifiedTotpFactorId } from './mfa';
import { signInWithPasskey, type PasskeySignInOptions } from './passkeys';
import { supabase } from './supabaseClient';
import { startTotpEnrollment, verifyTotpCode } from './totp';
import type { AuthContextValue } from './types';
import { clearUserScopedStorage, readLastUserId, writeLastUserId } from './userScopedStorage';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Module-level session storage for the token getter
// This is updated alongside React state to ensure the token getter always has access
let currentSession: Session | null = null;

/** The current access token, from the module-level session (no Supabase round-trip). */
async function getCurrentAccessToken(): Promise<string | null> {
  return currentSession?.access_token ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Ref for stable access in callbacks without adding to deps
  const sessionRef = useRef<Session | null>(null);

  // Helper to update both React state and module-level session
  const updateSession = useCallback((newSession: Session | null) => {
    currentSession = newSession;
    sessionRef.current = newSession;
    setSession(newSession);
  }, []);

  /** Drop every trace of the signed-in user's data from this tab. */
  const clearUserData = useCallback(() => {
    void queryClient.cancelQueries();
    queryClient.clear(); // query and mutation caches
    clearUserScopedStorage();
  }, [queryClient]);

  /** One exit path for every way a session can end. */
  const endSession = useCallback(() => {
    clearUserData();
    updateSession(null);
  }, [clearUserData, updateSession]);

  /**
   * Publish a (possibly new) session. When it belongs to a different user
   * than the last one seen in this browser, clear first — this covers a
   * session swap that never emitted SIGNED_OUT.
   */
  const adoptSession = useCallback((newSession: Session) => {
    const userId = newSession.user.id;
    const lastUserId = readLastUserId();
    const previousSessionUserId = sessionRef.current?.user.id;
    if ((lastUserId !== null && lastUserId !== userId) || (previousSessionUserId && previousSessionUserId !== userId)) {
      clearUserData();
    }
    writeLastUserId(userId);
    updateSession(newSession);
  }, [clearUserData, updateSession]);

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      endSession();
    }
  }, [endSession]);

  // Initialize session and listen for auth changes
  useEffect(() => {
    // Set up auth state change listener first - it will fire with INITIAL_SESSION
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, sess) => {
      // Handle token refresh errors by clearing the invalid session
      if (event === 'TOKEN_REFRESHED' && !sess) {
        console.warn('Token refresh failed - clearing invalid session');
        await supabase.auth.signOut();
        endSession();
        setIsLoading(false);
        return;
      }

      // Handle sign-out events
      if (event === 'SIGNED_OUT' || !sess) {
        if (event === 'SIGNED_OUT' || sessionRef.current) {
          endSession();
        }
        setIsLoading(false);
        return;
      }

      adoptSession(sess);

      // INITIAL_SESSION event means we've checked for existing session
      // Other events (SIGNED_IN, TOKEN_REFRESHED) also complete loading
      setIsLoading(false);
    });

    // Also call getSession as a backup (some Supabase versions need this)
    supabase.auth.getSession().then(({ data: { session: sess }, error }) => {
      if (error) {
        console.error('Failed to get session:', error.message);
        // If we get a refresh token error, clear the invalid session
        if (error.message?.includes('refresh_token') || error.message?.includes('Invalid Refresh Token')) {
          console.warn('Invalid refresh token detected - clearing session');
          void supabase.auth.signOut();
          endSession();
        }
      }
      if (sess) {
        adoptSession(sess);
      }
      setIsLoading(false);
    });

    // Proactively refresh the session when the app returns from background.
    // Browser timers are suspended when backgrounded, so Supabase's auto-refresh
    // may have missed its window. This ensures a valid token before queries fire.
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session: refreshedSession } }) => {
          if (refreshedSession) {
            adoptSession(refreshedSession);
          }
        });
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [adoptSession, endSession]);

  // Set up the token getter for the API client (once on mount)
  useEffect(() => {
    setTokenGetter(getCurrentAccessToken);
  }, []);

  // Set up the token refresher for the API client's 401 retry logic.
  // When a request gets a 401, the client will call this to attempt a
  // Supabase session refresh before giving up and logging out.
  useEffect(() => {
    setTokenRefresher(async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) {
        return null;
      }
      adoptSession(data.session);
      return data.session.access_token;
    });
  }, [adoptSession]);

  // Set up the logout handler for automatic logout on 401 errors
  // (the refresh above failed): the same session-end path as a manual logout.
  useEffect(() => {
    setLogoutHandler(() => {
      void logout();
    });
  }, [logout]);

  const sessionUserId = session?.user.id ?? null;
  const mfaStep = useMemo(() => (session ? mfaStepFor(session) : null), [session]);
  const isAuthenticated = !!session && mfaStep === null;
  const isTwoFactorVerified = useMemo(() => !!session && isTwoFactorSession(session), [session]);
  const isPasswordResetSession = useMemo(() => !!session && signedInFromEmailLink(session), [session]);
  const verifiedUserId = isAuthenticated ? sessionUserId : null;

  // Warm the profile query once per signed-in (two-factor verified) user.
  // Components read it with useCurrentUser()/useUserProfile();
  // ensureQueryData de-duplicates with them, so a cold load issues one
  // GET /auth/me (WEBD-24).
  useEffect(() => {
    if (!verifiedUserId) return;
    let cancelled = false;
    void (async () => {
      try {
        const profile = await queryClient.ensureQueryData(userProfileQueryOptions);
        // A profile created before Supabase shared the email carries a
        // placeholder; replace it with the session's real address once.
        const realEmail = sessionRef.current?.user?.email;
        if (!cancelled && realEmail && profile.email?.includes('@supabase.user')) {
          const updated = await authApi.updateMe({ email: realEmail });
          if (!cancelled) queryClient.setQueryData(userKeys.profile(), updated);
        }
      } catch (error) {
        // The query holds the error; readers show it (useUserProfile().isError).
        console.error('Failed to load user profile:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [verifiedUserId, queryClient]);

  const login = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    // Immediately update session state (don't wait for listener)
    if (data.session) {
      adoptSession(data.session);
    }
  }, [adoptSession]);

  const loginWithPasskey = useCallback(async (options?: PasskeySignInOptions) => {
    const session = await signInWithPasskey(options);
    // Signed in: a passkey needs no authenticator code (see mfaStepFor).
    adoptSession(session);
  }, [adoptSession]);

  /**
   * Verify a TOTP code, then publish the aal2 session Supabase stored (rather
   * than the MFA_CHALLENGE_VERIFIED event's payload, which lacks expires_at).
   */
  const completeSecondFactor = useCallback(async (factorId: string, code: string) => {
    await verifyTotpCode(factorId, code);
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    if (data.session) {
      adoptSession(data.session);
    }
  }, [adoptSession]);

  const verifyTotpChallenge = useCallback(async (code: string) => {
    const factorId = sessionRef.current ? verifiedTotpFactorId(sessionRef.current) : null;
    if (!factorId) {
      throw new Error('No authenticator app is set up for this account.');
    }
    await completeSecondFactor(factorId, code);
  }, [completeSecondFactor]);

  const value = useMemo<AuthContextValue>(() => ({
    sessionUserId,
    isLoading,
    isAuthenticated,
    isTwoFactorVerified,
    isPasswordResetSession,
    mfaStep,
    login,
    loginWithPasskey,
    logout,
    getAccessToken: getCurrentAccessToken,
    startTotpEnrollment,
    confirmTotpEnrollment: completeSecondFactor,
    verifyTotpChallenge,
  }), [sessionUserId, isLoading, isAuthenticated, isTwoFactorVerified, isPasswordResetSession, mfaStep, login, loginWithPasskey, logout, completeSecondFactor, verifyTotpChallenge]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
